/**
 * Generador de reportes PDF para Clima Organizacional.
 * Ejecución 100% en el cliente (sin backend).
 * Usa html2canvas para capturar gráficos y jspdf-autotable para tablas coloreadas.
 */

import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { DimensionScore, HeatmapRow } from '@/lib/climateScoring';
import { LIKERT_DIMENSIONS, getClimateInterpretation, getEnpsInterpretation } from '@/lib/climateScoring';
import type { ClimateDimension } from '@/config/climateQuestions';

const DIMENSION_LABELS: Record<ClimateDimension, string> = {
  leadership: 'Liderazgo',
  communication: 'Comunicación',
  growth: 'Crecimiento',
  wellbeing: 'Bienestar',
  belonging: 'Pertenencia',
  loyalty: 'Lealtad',
  general: 'General',
};

export interface ClimateSurvey {
  title: string;
  date: string;
  status: string;
}

export interface ClimateStats {
  globalScore: number;
  enps: { score: number; label: string };
  participation: { percent: number; completed: number; total: number };
}

export interface ClimateComment {
  department?: string;
  text: string;
}

export interface GenerateClimatePdfOptions {
  survey: ClimateSurvey;
  stats: ClimateStats;
  dimensionScores: DimensionScore[];
  heatmapData: HeatmapRow[];
  comments: ClimateComment[];
  logoUrl?: string;
}

/** Carga una imagen desde URL y la convierte a Base64. Intenta fetch primero (CORS), luego Image+canvas. */
async function loadImageAsBase64(url: string): Promise<string | null> {
  // Intento 1: fetch (funciona si CORS está configurado en Storage)
  try {
    const res = await fetch(url, { mode: 'cors' });
    if (!res.ok) throw new Error('Fetch failed');
    const blob = await res.blob();
    return await new Promise<string | null>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = () => reject(new Error('FileReader failed'));
      reader.readAsDataURL(blob);
    });
  } catch {
    // Intento 2: Image + canvas (fallback)
  }

  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      try {
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          resolve(null);
          return;
        }
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

/** Captura un elemento DOM y lo convierte a PNG Base64. */
async function captureElementToBase64(element: HTMLElement): Promise<string> {
  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
  });
  return canvas.toDataURL('image/png');
}

/** Genera y descarga el PDF de resultados de clima organizacional. */
export async function generateClimatePdf(options: GenerateClimatePdfOptions): Promise<void> {
  const { survey, stats, dimensionScores, heatmapData, comments, logoUrl: clientLogoUrl } = options;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  let y = margin;

  // ─── Paso 1: Logos (PsyTalent + cliente) ───
  const psyTalentLogoUrl = typeof window !== 'undefined' ? `${window.location.origin}/isotipo.png` : '/isotipo.png';
  const [psyLogoData, clientLogoData] = await Promise.all([
    loadImageAsBase64(psyTalentLogoUrl),
    clientLogoUrl ? loadImageAsBase64(clientLogoUrl) : Promise.resolve(null),
  ]);

  const logoHeight = 14;
  const clientLogoWidth = 40;
  const imgFormat = (dataUrl: string) => (dataUrl.startsWith('data:image/jpeg') || dataUrl.startsWith('data:image/jpg') ? 'JPEG' : 'PNG');
  try {
    if (psyLogoData) {
      doc.addImage(psyLogoData, imgFormat(psyLogoData), margin, y, logoHeight, logoHeight);
    }
    if (clientLogoData) {
      doc.addImage(clientLogoData, imgFormat(clientLogoData), pageWidth - margin - clientLogoWidth, y, clientLogoWidth, logoHeight);
    }
  } catch (e) {
    if (import.meta.env?.DEV) console.warn('[Climate PDF] Error adding logos:', e);
  }
  y += logoHeight + 6;

  // ─── Paso 2: Título y fecha ───
  doc.setFontSize(22);
  doc.setFont('helvetica', 'bold');
  doc.text('PsyTalent', margin, y);
  y += 8;

  doc.setFontSize(16);
  doc.text(survey.title, margin, y);
  y += 6;

  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text(`Fecha: ${survey.date} · Estado: ${survey.status}`, margin, y);
  doc.setTextColor(0, 0, 0);
  y += 12;

  // ─── Paso 3: KPIs ───
  const kpiWidth = (pageWidth - margin * 2 - 10) / 3;
  const kpiHeight = 45;

  const climateInterp = getClimateInterpretation(stats.globalScore);
  const enpsInterp = getEnpsInterpretation(stats.enps.score);

  const rgbFromColor = (color: string): [number, number, number] => {
    if (color.includes('red')) return [220, 38, 38];
    if (color.includes('yellow')) return [202, 138, 4];
    if (color.includes('green-500')) return [34, 197, 94];
    if (color.includes('green-700')) return [21, 128, 61];
    return [34, 197, 94];
  };

  // Índice de Clima
  doc.setFillColor(248, 250, 252);
  doc.rect(margin, y, kpiWidth, kpiHeight, 'F');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text('Índice de Clima', margin + 5, y + 8);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...rgbFromColor(climateInterp.color));
  doc.text(`${stats.globalScore}`, margin + 5, y + 18);
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text('/ 100', margin + 5, y + 23);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...rgbFromColor(climateInterp.color));
  doc.text(climateInterp.label, margin + 5, y + 30);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  const climateDescLines = doc.splitTextToSize(climateInterp.description, kpiWidth - 10);
  doc.text(climateDescLines, margin + 5, y + 36);

  // eNPS
  doc.setFillColor(248, 250, 252);
  doc.rect(margin + kpiWidth + 5, y, kpiWidth, kpiHeight, 'F');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text('eNPS', margin + kpiWidth + 10, y + 8);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...rgbFromColor(enpsInterp.color));
  doc.text(`${stats.enps.score >= 0 ? '+' : ''}${stats.enps.score}`, margin + kpiWidth + 10, y + 18);
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...rgbFromColor(enpsInterp.color));
  doc.text(enpsInterp.label, margin + kpiWidth + 10, y + 25);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(80, 80, 80);
  const enpsDescLines = doc.splitTextToSize(enpsInterp.description, kpiWidth - 10);
  doc.text(enpsDescLines, margin + kpiWidth + 10, y + 31);

  // Participación
  doc.setFillColor(248, 250, 252);
  doc.rect(margin + (kpiWidth + 5) * 2, y, kpiWidth, kpiHeight, 'F');
  doc.setFontSize(9);
  doc.setTextColor(100, 116, 139);
  doc.text('Participación', margin + (kpiWidth + 5) * 2 + 5, y + 8);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text(`${stats.participation.percent}%`, margin + (kpiWidth + 5) * 2 + 5, y + 18);
  doc.setFontSize(8);
  doc.setTextColor(100, 116, 139);
  doc.text(`${stats.participation.completed} / ${stats.participation.total} empleados`, margin + (kpiWidth + 5) * 2 + 5, y + 24);

  doc.setTextColor(0, 0, 0);
  y += kpiHeight + 12;

  // ─── Paso 4: Imágenes de gráficos ───
  const chartWidth = (pageWidth - margin * 2 - 5) / 2;
  const chartHeight = 55;

  const radarEl = document.getElementById('climate-radar-chart');
  const barEl = document.getElementById('climate-bar-chart');

  if (radarEl && dimensionScores.length > 0) {
    try {
      const radarImg = await captureElementToBase64(radarEl);
      doc.addImage(radarImg, 'PNG', margin, y, chartWidth, chartHeight);
    } catch (e) {
      doc.setFontSize(10);
      doc.text('Gráfico Radar (no disponible)', margin, y + chartHeight / 2);
    }
  }

  if (barEl && heatmapData.length > 0) {
    try {
      const barImg = await captureElementToBase64(barEl);
      doc.addImage(barImg, 'PNG', margin + chartWidth + 5, y, chartWidth, chartHeight);
    } catch (e) {
      doc.setFontSize(10);
      doc.text('Gráfico de Barras (no disponible)', margin + chartWidth + 5, y + chartHeight / 2);
    }
  }

  if (radarEl || barEl) {
    y += chartHeight + 12;
  }

  // ─── Paso 5: Tabla Heatmap con jspdf-autotable ───
  if (heatmapData.length > 0) {
    const columns = ['Departamento', ...LIKERT_DIMENSIONS.map((d) => DIMENSION_LABELS[d]), 'Promedio'];
    const head = [columns];
    const body = heatmapData.map((row) => {
      const cells: (string | number)[] = [row.department];
      for (const dim of LIKERT_DIMENSIONS) {
        const v = row.scores[dim];
        cells.push(v != null ? v : '—');
      }
      cells.push(row.average);
      return cells;
    });

    autoTable(doc, {
      head,
      body,
      startY: y,
      theme: 'grid',
      headStyles: { fillColor: [59, 130, 246], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 8 },
      columnStyles: {
        0: { cellWidth: 35 },
      },
      didParseCell: (data) => {
        if (data.section === 'body' && data.column.index > 0) {
          const raw = data.cell.raw;
          const val = typeof raw === 'number' ? raw : typeof raw === 'string' ? parseFloat(raw) : NaN;
          if (!Number.isNaN(val)) {
            if (val < 60) {
              data.cell.styles.fillColor = [252, 165, 165]; // rojo suave
            } else if (val > 80) {
              data.cell.styles.fillColor = [134, 239, 172]; // verde suave
            } else {
              data.cell.styles.fillColor = [254, 249, 195]; // amarillo suave
            }
          }
        }
      },
    });

    y = (doc as jsPDF & { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? y;
    y += 10;
  }

  // ─── Paso 6: Comentarios ───
  if (comments.length > 0) {
    if (y > 250) {
      doc.addPage();
      y = margin;
    }

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.text('Comentarios Abiertos', margin, y);
    y += 8;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');

    for (const c of comments) {
      if (y > 270) {
        doc.addPage();
        y = margin;
      }

      if (c.department) {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(8);
        doc.setTextColor(100, 116, 139);
        doc.text(c.department, margin, y);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(9);
        doc.setTextColor(0, 0, 0);
        y += 5;
      }

      const lines = doc.splitTextToSize(c.text, pageWidth - margin * 2);
      doc.text(lines, margin, y);
      y += lines.length * 5 + 6;
    }
  }

  // ─── Paso 7: Descarga ───
  const dateStr = new Date().toISOString().slice(0, 10);
  doc.save(`Clima_Organizacional_Resultados_${dateStr}.pdf`);
}
